import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCurriculum } from "@/context/CurriculumContext";
import { SUBJECT_META, levelStyle, overallBand, BAND_STYLE } from "@/lib/scale";
import { LayoutGrid, Loader2 } from "lucide-react";

const BANDS = [["emerging", "Emerging 1–3"], ["developing", "Developing 4–6"], ["secure", "Secure 7–8"], ["advanced", "Advanced 9"]];

function HeatmapLegend() {
  return (
    <div className="flex items-center gap-4 mt-4 flex-wrap text-xs text-slate-500">
      {BANDS.map(([k, label]) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded" style={{ backgroundColor: BAND_STYLE[k].solid }} />
          {label}
        </span>
      ))}
    </div>
  );
}

function HeatmapRow({ row, subject, domains, onOpen }) {
  const latest = row.latest?.[subject];
  return (
    <tr data-testid={`heatmap-row-${row.id}`} onClick={onOpen} className="cursor-pointer group">
      <td className="py-1 pr-3 font-semibold text-slate-700 text-sm whitespace-nowrap sticky left-0 bg-white group-hover:text-[#4A5D6B]">
        {row.name}
      </td>
      {domains.map((d) => {
        const lvl = latest?.scores?.[d.key];
        const st = lvl ? levelStyle(lvl) : null;
        return (
          <td key={d.key} className="px-1 py-1">
            <div
              className="h-10 rounded-lg flex items-center justify-center font-['Outfit'] font-bold text-sm transition-transform group-hover:scale-[1.04]"
              style={lvl ? { backgroundColor: st.solid, color: "#fff" } : { backgroundColor: "#F1F3F1", color: "#cbd5e1" }}
              title={lvl ? `${d.name}: Level ${lvl}` : `${d.name}: not assessed`}
            >
              {lvl || "–"}
            </div>
          </td>
        );
      })}
      <td className="pl-2 py-1">
        {latest ? (
          <div
            className="h-10 rounded-lg flex items-center justify-center font-['Outfit'] font-bold text-sm"
            style={{ backgroundColor: BAND_STYLE[overallBand(latest.total).key].soft, color: "#334155" }}
          >
            {latest.total}<span className="text-[10px] text-slate-400 ml-0.5">/45</span>
          </div>
        ) : (
          <div className="h-10 rounded-lg bg-[#F1F3F1] flex items-center justify-center text-slate-300">–</div>
        )}
      </td>
    </tr>
  );
}

export default function ClassHeatmap({ classId, refreshKey }) {
  const navigate = useNavigate();
  const curriculum = useCurriculum();
  const [rows, setRows] = useState(null);
  const [subject, setSubject] = useState("literacy");

  useEffect(() => {
    api.get(`/classes/${classId}/overview`).then((r) => setRows(r.data.students)).catch(() => setRows([]));
  }, [classId, refreshKey]);

  if (!curriculum) return null;
  const subj = curriculum.subjects[subject];
  const domains = subj.domains;
  const tint = SUBJECT_META[subject].tint;

  const anyAssessed = rows && rows.some((r) => r.latest && r.latest[subject]);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 mb-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 className="font-['Outfit'] text-2xl font-bold text-slate-800 flex items-center gap-2">
          <LayoutGrid size={22} className="text-[#5d8a73]" /> Class Overview
        </h2>
        <div className="flex gap-1.5 bg-[#F9F9F7] p-1 rounded-full">
          {Object.keys(curriculum.subjects).filter((k) => !curriculum.subjects[k].optional).map((k) => (
            <button
              key={k}
              data-testid={`heatmap-subject-${k}`}
              onClick={() => setSubject(k)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold font-['Outfit'] transition ${
                subject === k ? "text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
              style={subject === k ? { backgroundColor: SUBJECT_META[k].tint } : {}}
            >
              {SUBJECT_META[k].name.split(" ")[0]}
            </button>
          ))}
        </div>
      </div>

      {rows === null && (
        <div className="flex justify-center py-10 text-slate-300"><Loader2 className="animate-spin" /></div>
      )}
      {rows !== null && rows.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-6">Add students to see the class overview.</p>
      )}
      {rows !== null && rows.length > 0 && !anyAssessed && (
        <p className="text-sm text-slate-400 text-center py-6">No {subj.name.toLowerCase()} assessments recorded yet.</p>
      )}
      {rows !== null && rows.length > 0 && anyAssessed && (
        <div className="overflow-x-auto">
          <table className="w-full border-separate" style={{ borderSpacing: "0 4px" }}>
            <thead>
              <tr>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-3 sticky left-0 bg-white">Student</th>
                {domains.map((d) => (
                  <th key={d.key} className="text-center text-[10px] font-bold uppercase tracking-wide text-slate-400 pb-2 px-1 min-w-[64px]">
                    {d.name.split(" ")[0]}
                  </th>
                ))}
                <th className="text-center text-[10px] font-bold uppercase tracking-wide text-slate-400 pb-2 pl-2">Overall</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <HeatmapRow key={r.id} row={r} subject={subject} domains={domains} onOpen={() => navigate(`/student/${r.id}`)} />
              ))}
            </tbody>
          </table>

          <HeatmapLegend />
        </div>
      )}
    </div>
  );
}
